import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import DataPagination from '../components/DataPagination';
import api from '../lib/api';
import { Shield, AlertTriangle, CheckCircle, XCircle, Globe, Monitor, Search, RefreshCw, Clock } from 'lucide-react';
import { toast } from 'sonner';

const LoginAudit = () => {
  const { isSuperAdmin } = useAuth();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize] = useState(20);
  
  // Filters
  const [successFilter, setSuccessFilter] = useState('all');
  const [emailSearch, setEmailSearch] = useState('');

  useEffect(() => {
    if (isSuperAdmin()) {
      fetchLogs();
      fetchStats();
    }
  }, [currentPage, successFilter]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, page_size: pageSize };
      if (successFilter !== 'all') {
        params.success = successFilter === 'success';
      }
      if (emailSearch) {
        params.email = emailSearch;
      }
      
      const response = await api.get('/admin/login-logs', { params });
      setLogs(response.data.items || []);
      setTotalPages(response.data.total_pages || 1);
      setTotalItems(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch login logs:', error);
      toast.error('Failed to fetch login logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/login-stats', { params: { days: 7 } });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch login stats:', error);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchLogs();
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const getStatusBadge = (log) => {
    if (!log.success) {
      return <Badge variant="destructive" className="bg-red-100 text-red-700 border-red-200">Failed</Badge>;
    }
    if (log.requires_2fa) {
      return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">2FA Required</Badge>;
    }
    return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Success</Badge>;
  };

  if (!isSuperAdmin()) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-12 text-center">
            <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-700">Access Denied</h2>
            <p className="text-slate-500 mt-2">Only Super Admins can view login audit logs.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Login Audit Logs</h1>
          <p className="text-slate-500 mt-1">Monitor login attempts and security events</p>
        </div>
        <Button onClick={() => { fetchLogs(); fetchStats(); }} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Globe className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_attempts}</p>
                  <p className="text-xs text-slate-500">Total Attempts (7d)</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">{stats.successful_logins}</p>
                  <p className="text-xs text-slate-500">Successful Logins</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-600">{stats.failed_attempts}</p>
                  <p className="text-xs text-slate-500">Failed Attempts</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-yellow-600">{stats.twofa_triggered}</p>
                  <p className="text-xs text-slate-500">2FA Triggered</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Suspicious IPs Alert */}
      {stats?.suspicious_ips?.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Suspicious IP Addresses (Multiple Failed Attempts)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {stats.suspicious_ips.map((ip, idx) => (
                <Badge key={idx} variant="outline" className="bg-white border-red-300 text-red-700">
                  {ip.ip} ({ip.failed_count} failed)
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium text-slate-700 mb-1 block">Search Email</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by email..."
                  value={emailSearch}
                  onChange={(e) => setEmailSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-9"
                />
              </div>
            </div>
            
            <div className="w-[150px]">
              <label className="text-sm font-medium text-slate-700 mb-1 block">Status</label>
              <Select value={successFilter} onValueChange={setSuccessFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="success">Success</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Button onClick={handleSearch} className="bg-blue-600 hover:bg-blue-700">
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Login History ({totalItems} records)</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-500">Loading...</div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8 text-slate-500">No login logs found</div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>IP Address</TableHead>
                    <TableHead>Device</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Timestamp</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id} className={!log.success ? 'bg-red-50/50' : ''}>
                      <TableCell>{getStatusBadge(log)}</TableCell>
                      <TableCell className="font-medium">{log.email}</TableCell>
                      <TableCell className="text-slate-600">{log.user_name || '-'}</TableCell>
                      <TableCell>
                        <code className="text-xs bg-slate-100 px-2 py-1 rounded">{log.ip_address}</code>
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate text-xs text-slate-500" title={log.user_agent}>
                        {log.user_agent?.substring(0, 40)}...
                      </TableCell>
                      <TableCell className="text-red-600 text-sm">{log.failure_reason || '-'}</TableCell>
                      <TableCell className="text-slate-500 text-sm">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDateTime(log.timestamp)}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {totalPages > 1 && (
                <div className="mt-4">
                  <DataPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginAudit;
